import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ordersAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Package, Calendar, CheckCircle, Clock } from 'lucide-react';

const LocalHistoryPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await ordersAPI.getHistory(30);
      setOrders(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="local-history-page">
      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">Historial</h2>
        <p className="text-sm text-muted-foreground mt-1">Últimos 30 días</p>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-muted-foreground">No hay pedidos registrados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
              data-testid={`history-order-${index}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">
                      {formatDate(order.order_date)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {order.items.length} productos
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {order.status === 'submitted' || order.status === 'closed' ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      Enviado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                      <Clock className="w-3 h-3" />
                      Borrador
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2 border-t border-slate-100 pt-3">
                {order.items.slice(0, 3).map((item) => (
                  <div key={item.item_id} className="flex justify-between text-sm">
                    <span className="text-slate-700">{item.item_name}</span>
                    <span className="font-medium text-slate-900">
                      {item.requested_quantity} {item.unit}
                    </span>
                  </div>
                ))}
                {order.items.length > 3 && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    +{order.items.length - 3} productos más
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalHistoryPage;